import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * ✅ PortfolioPage
 * - Auto detect subdomain (abid / asim / etc)
 * - Render template sama, content berbeza
 * - Download PDF (multi-page, A4)
 *
 * Notes:
 * - Untuk localhost dev, boleh pakai: http://localhost:3000/?sub=abid
 */

const PORTFOLIOS = {
    abid: {
        slug: "abid",
        displayName: "Abid Rifaie",
        roleTag: "Student Portfolio",
        headline: "Seorang pelajar yang aktif, disiplin, dan suka cabaran.",
        avatar: "/portfolio/abid/avatar.jpg", // letak dalam public/portfolio/abid/avatar.jpg
        cover: "/portfolio/abid/cover.jpg",

        biodata: {
            fullName: "Abid Rifaie",
            dob: "2010-04-12",
            school: "Sekolah Kebangsaan Contoh",
            location: "Selangor, Malaysia",
            email: "abid@domain.com",
            phone: "+60 12-345 6789",
        },

        summary: [
            "Meminati aktiviti kepimpinan dan sukan.",
            "Aktif dalam ko-kurikulum dan projek sekolah.",
            "Mempunyai minat dalam teknologi dan kreativiti.",
        ],

        education: [
            {
                title: "Sekolah Kebangsaan Contoh",
                meta: "2017 – 2022",
                details: [
                    "Pengawas sekolah (2021–2022)",
                    "Kelas Aliran Perdana",
                    "Aktif program NILAM & kokurikulum",
                ],
            },
            {
                title: "Sekolah Menengah Contoh",
                meta: "2023 – Kini",
                details: [
                    "Ahli Kelab Robotik",
                    "Penyertaan pertandingan inovasi",
                    "Aktif dalam program kepimpinan",
                ],
            },
        ],

        cocurricular: [
            {
                title: "Kelab Robotik",
                meta: "Ahli aktif",
                details: ["Latihan mingguan", "Penyertaan pameran & pertandingan", "Kerja berpasukan"],
            },
            {
                title: "Sukan & Permainan",
                meta: "Penyertaan tahunan",
                details: ["Kejohanan sekolah", "Latihan kecergasan", "Disiplin & semangat pasukan"],
            },
        ],

        achievements: [
            { year: "2025", title: "Tokoh Ko-Kurikulum", org: "Sekolah", note: "Penglibatan konsisten dan pencapaian." },
            { year: "2024", title: "Penyertaan Pertandingan Robotik", org: "Daerah", note: "Pembentangan projek & teamwork." },
        ],

        skills: ["Kepimpinan", "Komunikasi", "Kerja Berpasukan", "Pengucapan", "Asas Teknologi"],

        gallery: [
            { type: "image", src: "/portfolio/abid/gallery/1.jpg", caption: "Aktiviti sekolah" },
            { type: "image", src: "/portfolio/abid/gallery/2.jpg", caption: "Program kokurikulum" },
            { type: "image", src: "/portfolio/abid/gallery/3.jpg", caption: "Penyertaan pertandingan" },
        ],

        videos: [
            { title: "Video Pembentangan Projek", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        ],
    },

    asim: {
        slug: "asim",
        displayName: "Asim",
        roleTag: "Student Portfolio",
        headline: "Minat belajar, suka eksplorasi, dan aktif dalam aktiviti sekolah.",
        avatar: "/portfolio/asim/avatar.jpg",
        cover: "/portfolio/asim/cover.jpg",

        biodata: {
            fullName: "Asim",
            dob: "2012-08-05",
            school: "Sekolah Kebangsaan Contoh 2",
            location: "Selangor, Malaysia",
            email: "asim@domain.com",
            phone: "+60 11-222 3333",
        },

        summary: ["Suka aktiviti luar & sukan.", "Minat membaca & projek kreatif.", "Aktif program sekolah."],

        education: [
            {
                title: "Sekolah Kebangsaan Contoh 2",
                meta: "2019 – Kini",
                details: ["Penglibatan dalam program NILAM", "Aktif kelas & aktiviti ko-kurikulum"],
            },
        ],

        cocurricular: [
            { title: "Kelab Bahasa", meta: "Ahli", details: ["Aktiviti mingguan", "Pertandingan bercerita"] },
        ],

        achievements: [
            { year: "2025", title: "Anugerah Kehadiran Penuh", org: "Sekolah", note: "Kehadiran konsisten sepanjang tahun." },
        ],

        skills: ["Disiplin", "Keyakinan diri", "Teamwork", "Kreativiti"],

        gallery: [
            { type: "image", src: "/portfolio/asim/gallery/1.jpg", caption: "Hari sukan" },
            { type: "image", src: "/portfolio/asim/gallery/2.jpg", caption: "Aktiviti sekolah" },
        ],

        videos: [],
    },
};

function getSubdomain() {
    const host = window.location.hostname.toLowerCase();

    // ✅ localhost dev support: ?sub=abid
    const sp = new URLSearchParams(window.location.search);
    const forced = (sp.get("sub") || "").toLowerCase().trim();
    if (forced) return forced;

    const parts = host.split(".").filter(Boolean);

    // ✅ treat *.localhost as subdomain in dev
    // abid.localhost -> ["abid","localhost"]
    if (parts.length === 2 && parts[1] === "localhost" && parts[0] !== "localhost") {
        return parts[0];
    }

    // ✅ production: abid.domain.com -> ["abid","domain","com"]
    if (parts.length <= 2) return ""; // domain.com (no subdomain)
    if (parts[0] === "www") return "";

    return parts[0];
}


function formatDob(dob) {
    if (!dob) return "-";
    try {
        const d = new Date(dob);
        return d.toLocaleDateString("ms-MY", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return dob;
    }
}

export default function PortfolioPage() {
    const subdomain = useMemo(() => getSubdomain(), []);
    const profile = PORTFOLIOS[subdomain] || null;

    const contentRef = useRef(null);
    const [downloading, setDownloading] = useState(false);

    const handleDownloadPdf = async () => {
        if (!contentRef.current) return;

        setDownloading(true);
        try {
            const el = contentRef.current;

            // improve capture quality
            const scale = Math.min(2, window.devicePixelRatio || 1);

            const canvas = await html2canvas(el, {
                scale,
                useCORS: true,
                backgroundColor: "#ffffff",
                scrollX: 0,
                scrollY: -window.scrollY,
            });

            const imgData = canvas.toDataURL("image/jpeg", 0.92);

            const pdf = new jsPDF("p", "mm", "a4");
            const pageWidth = 210;
            const pageHeight = 297;

            // image fit to page width
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 2) {
                pdf.addPage();
                position = heightLeft - imgHeight; // negative offset trick
                pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const safeName = (profile?.displayName || "portfolio").replace(/\s+/g, "_").toLowerCase();
            pdf.save(`${safeName}_portfolio.pdf`);
        } catch (e) {
            console.error(e);
            alert("PDF gagal dijana. Cuba semula.");
        } finally {
            setDownloading(false);
        }
    };

    if (!profile) {
        return (
            <div style={styles.shell}>
                <div style={styles.card}>
                    <h2 style={{ margin: 0 }}>Portfolio tidak dijumpai</h2>
                    <p style={{ marginTop: 8, color: "#555" }}>
                        Subdomain ini tiada dalam data portfolio. Tambah data dalam <code>PORTFOLIOS</code>.
                        <br />
                        (Untuk test localhost: <code>?sub=abid</code>)
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.shell}>
            {/* Topbar */}
            <div style={styles.topbar}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={styles.dot} />
                    <div>
                        <div style={styles.title}>{profile.displayName}</div>
                        <div style={styles.subtitle}>{profile.roleTag}</div>
                    </div>
                </div>

                <button
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    style={{
                        ...styles.btn,
                        opacity: downloading ? 0.7 : 1,
                        cursor: downloading ? "not-allowed" : "pointer",
                    }}
                >
                    {downloading ? "Generating PDF..." : "Download PDF"}
                </button>
            </div>

            {/* Content (this area becomes the PDF) */}
            <div ref={contentRef} style={styles.paper}>
                {/* Hero */}
                <div style={styles.hero}>
                    <div style={styles.coverWrap}>
                        {profile.cover ? (
                            <img src={profile.cover} alt="cover" style={styles.coverImg} />
                        ) : (
                            <div style={styles.coverPlaceholder} />
                        )}
                    </div>

                    <div style={styles.heroInner}>
                        <div style={styles.avatarWrap}>
                            {profile.avatar ? (
                                <img src={profile.avatar} alt="avatar" style={styles.avatar} />
                            ) : (
                                <div style={styles.avatarPlaceholder} />
                            )}
                        </div>

                        <div style={{ minWidth: 0 }}>
                            <h1 style={styles.h1}>{profile.displayName}</h1>
                            <p style={styles.lead}>{profile.headline}</p>

                            <div style={styles.kvGrid}>
                                <div style={styles.kvItem}>
                                    <div style={styles.k}>Nama</div>
                                    <div style={styles.v}>{profile.biodata.fullName || "-"}</div>
                                </div>
                                <div style={styles.kvItem}>
                                    <div style={styles.k}>Tarikh Lahir</div>
                                    <div style={styles.v}>{formatDob(profile.biodata.dob)}</div>
                                </div>
                                <div style={styles.kvItem}>
                                    <div style={styles.k}>Sekolah</div>
                                    <div style={styles.v}>{profile.biodata.school || "-"}</div>
                                </div>
                                <div style={styles.kvItem}>
                                    <div style={styles.k}>Lokasi</div>
                                    <div style={styles.v}>{profile.biodata.location || "-"}</div>
                                </div>
                                <div style={styles.kvItem}>
                                    <div style={styles.k}>Email</div>
                                    <div style={styles.v}>{profile.biodata.email || "-"}</div>
                                </div>
                                <div style={styles.kvItem}>
                                    <div style={styles.k}>Telefon</div>
                                    <div style={styles.v}>{profile.biodata.phone || "-"}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary */}
                <Section title="Ringkasan Diri">
                    <div style={styles.grid2}>
                        <Card>
                            <h3 style={styles.h3}>Perkara utama</h3>
                            <ul style={styles.ul}>
                                {(profile.summary || []).map((x, i) => (
                                    <li key={i} style={styles.li}>
                                        {x}
                                    </li>
                                ))}
                            </ul>
                        </Card>

                        <Card>
                            <h3 style={styles.h3}>Kemahiran</h3>
                            <div style={styles.chips}>
                                {(profile.skills || []).map((s, i) => (
                                    <span key={i} style={styles.chip}>
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </Card>
                    </div>
                </Section>

                {/* Education */}
                <Section title="Pendidikan">
                    <div style={styles.stack}>
                        {(profile.education || []).map((ed, i) => (
                            <Card key={i}>
                                <div style={styles.rowBetween}>
                                    <div>
                                        <div style={styles.itemTitle}>{ed.title}</div>
                                        <div style={styles.itemMeta}>{ed.meta}</div>
                                    </div>
                                </div>
                                <ul style={styles.ul}>
                                    {(ed.details || []).map((d, j) => (
                                        <li key={j} style={styles.li}>
                                            {d}
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        ))}
                    </div>
                </Section>

                {/* Cocurricular */}
                <Section title="Ko-Kurikulum">
                    <div style={styles.stack}>
                        {(profile.cocurricular || []).map((cc, i) => (
                            <Card key={i}>
                                <div style={styles.rowBetween}>
                                    <div>
                                        <div style={styles.itemTitle}>{cc.title}</div>
                                        <div style={styles.itemMeta}>{cc.meta}</div>
                                    </div>
                                </div>
                                <ul style={styles.ul}>
                                    {(cc.details || []).map((d, j) => (
                                        <li key={j} style={styles.li}>
                                            {d}
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        ))}
                    </div>
                </Section>

                {/* Achievements */}
                <Section title="Pencapaian & Anugerah">
                    <Card>
                        <div style={styles.table}>
                            <div style={{ ...styles.tr, ...styles.thRow }}>
                                <div style={styles.th}>Tahun</div>
                                <div style={styles.th}>Pencapaian</div>
                                <div style={styles.th}>Organisasi</div>
                                <div style={styles.th}>Catatan</div>
                            </div>

                            {(profile.achievements || []).map((a, i) => (
                                <div key={i} style={styles.tr}>
                                    <div style={styles.td}>{a.year}</div>
                                    <div style={styles.td}>{a.title}</div>
                                    <div style={styles.td}>{a.org}</div>
                                    <div style={styles.td}>{a.note}</div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Section>

                {/* Gallery */}
                <Section title="Galeri">
                    <div style={styles.gallery}>
                        {(profile.gallery || []).map((g, i) => (
                            <div key={i} style={styles.gItem}>
                                {g.type === "image" ? (
                                    <img src={g.src} alt={g.caption || "gallery"} style={styles.gImg} />
                                ) : (
                                    <div style={styles.gPlaceholder} />
                                )}
                                <div style={styles.gCap}>{g.caption || ""}</div>
                            </div>
                        ))}
                    </div>
                    <div style={styles.smallNote}>
                        Tip: letak semua gambar dalam <code>public/portfolio/{profile.slug}/gallery/</code>
                    </div>
                </Section>

                {/* Videos */}
                {!!(profile.videos || []).length && (
                    <Section title="Video">
                        <div style={styles.stack}>
                            {(profile.videos || []).map((v, i) => (
                                <Card key={i}>
                                    <div style={styles.itemTitle}>{v.title}</div>
                                    <div style={{ marginTop: 10, borderRadius: 14, overflow: "hidden" }}>
                                        <iframe
                                            title={v.title}
                                            src={v.url}
                                            width="100%"
                                            height="340"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            style={{ display: "block" }}
                                        />
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </Section>
                )}

                {/* Footer note */}
                <div style={styles.footerNote}>
                    <div style={{ fontWeight: 700 }}>{profile.displayName}</div>
                    <div style={{ color: "#666" }}>Portfolio / Hardcopy PDF</div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginTop: 18 }}>
            <div style={styles.sectionTitle}>{title}</div>
            <div style={{ marginTop: 10 }}>{children}</div>
        </div>
    );
}

function Card({ children }) {
    return <div style={styles.card}>{children}</div>;
}

const styles = {
    shell: {
        minHeight: "100vh",
        background: "#f6f7fb",
        padding: "18px 14px 44px",
        boxSizing: "border-box",
    },
    topbar: {
        maxWidth: 1040,
        margin: "0 auto 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 18,
        background: "rgba(255,255,255,0.85)",
        border: "1px solid rgba(0,0,0,0.06)",
        backdropFilter: "blur(10px)",
    },
    dot: {
        width: 10,
        height: 10,
        borderRadius: 999,
        background: "#111",
    },
    title: { fontWeight: 900, fontSize: 16, letterSpacing: 0.2 },
    subtitle: { fontSize: 12.5, color: "#666", marginTop: 2 },

    btn: {
        border: "1px solid rgba(0,0,0,0.12)",
        background: "#111",
        color: "#fff",
        padding: "10px 12px",
        borderRadius: 12,
        fontWeight: 800,
        fontSize: 13,
    },

    paper: {
        maxWidth: 1040,
        margin: "0 auto",
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: 22,
        overflow: "hidden",
        boxShadow: "0 10px 26px rgba(10,12,20,0.08)",
    },

    hero: {
        position: "relative",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
    },
    coverWrap: {
        height: 220,
        background: "#0f172a",
    },
    coverImg: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
    },
    coverPlaceholder: {
        width: "100%",
        height: "100%",
        background:
            "linear-gradient(135deg, rgba(15,23,42,1) 0%, rgba(30,41,59,1) 50%, rgba(15,23,42,1) 100%)",
    },
    heroInner: {
        display: "flex",
        gap: 16,
        padding: "16px 18px 18px",
        alignItems: "flex-start",
        marginTop: -46,
    },
    avatarWrap: {
        width: 110,
        minWidth: 110,
        height: 110,
        borderRadius: 22,
        overflow: "hidden",
        border: "4px solid #fff",
        boxShadow: "0 8px 22px rgba(0,0,0,0.12)",
        background: "#eee",
    },
    avatar: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
    avatarPlaceholder: { width: "100%", height: "100%", background: "#e5e7eb" },

    h1: { margin: "2px 0 6px", fontSize: 28, letterSpacing: -0.4 },
    lead: { margin: 0, color: "#444", lineHeight: 1.5, maxWidth: 740 },

    kvGrid: {
        marginTop: 12,
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 10,
    },
    kvItem: {
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 14,
        padding: "10px 10px",
        background: "#fafafa",
    },
    k: { fontSize: 11.5, color: "#666", fontWeight: 800, marginBottom: 4 },
    v: { fontSize: 13.5, fontWeight: 800, color: "#111" },

    sectionTitle: {
        padding: "0 18px",
        fontSize: 13,
        fontWeight: 900,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "#111",
    },

    card: {
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 18,
        background: "#fff",
        padding: "14px 14px",
    },

    grid2: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        padding: "0 18px",
    },
    stack: { display: "grid", gap: 12, padding: "0 18px" },

    h3: { margin: "0 0 10px", fontSize: 15, letterSpacing: -0.2 },
    ul: { margin: 0, paddingLeft: 18, color: "#222" },
    li: { marginBottom: 6, lineHeight: 1.5 },

    chips: { display: "flex", flexWrap: "wrap", gap: 8 },
    chip: {
        border: "1px solid rgba(0,0,0,0.10)",
        background: "#f6f7fb",
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 800,
    },

    rowBetween: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 },
    itemTitle: { fontWeight: 900, fontSize: 15, letterSpacing: -0.2 },
    itemMeta: { fontSize: 12.5, color: "#666", marginTop: 4 },

    table: { width: "100%", display: "grid", gap: 0 },
    tr: {
        display: "grid",
        gridTemplateColumns: "0.7fr 1.4fr 1fr 1.6fr",
        gap: 10,
        padding: "10px 10px",
        borderTop: "1px solid rgba(0,0,0,0.06)",
    },
    thRow: { borderTop: "none", background: "#fafafa", borderRadius: 14 },
    th: { fontSize: 11.5, fontWeight: 900, color: "#111", textTransform: "uppercase", letterSpacing: 0.5 },
    td: { fontSize: 13, color: "#111", lineHeight: 1.4 },

    gallery: {
        padding: "0 18px",
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 12,
    },
    gItem: {
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 18,
        overflow: "hidden",
        background: "#fff",
    },
    gImg: { width: "100%", height: 190, objectFit: "cover", display: "block" },
    gPlaceholder: { width: "100%", height: 190, background: "#e5e7eb" },
    gCap: { padding: "10px 10px", fontSize: 12.5, color: "#333", fontWeight: 700 },

    smallNote: { padding: "10px 18px 0", fontSize: 12.5, color: "#666" },

    footerNote: {
        marginTop: 26,
        padding: "18px 18px 22px",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
    },
};
